/**
 * Bidi-safe ranges.
 *
 * A price range like "₪1.6M - ₪2.5M" contains no Hebrew at all: digits, a dot, a
 * hyphen, the shekel sign and the Latin "M". To the Unicode bidi algorithm that is
 * a run of numbers, neutrals and ONE strong left-to-right letter (the "M"), so on an
 * RTL page the pair was laid out left-to-right — the low bound on the LEFT, which
 * reads backwards next to the Hebrew label beside it.
 *
 * `formatBidiRange` fixes the ORDER without touching either bound: each side is
 * wrapped in an LTR isolate, so its own digits and currency sign keep their
 * left-to-right shape while the pair itself is laid out in the direction of the
 * surrounding element — low bound on the right in RTL, on the left in LTR, with no
 * locale check in the caller.
 *
 * The container must therefore NOT force `direction: ltr` (two chips used to), or
 * the isolates have nothing to follow and the old order comes back.
 */
// Written as escapes, never literal characters — both are invisible, and an editor,
// a copy-paste or a lint autofix would silently lose them.
const LTR_ISOLATE = "\u2066";
const POP_DIRECTIONAL_ISOLATE = "\u2069";

/** Keeps `text` internally left-to-right whatever the surrounding direction is. */
export const isolateLtr = (text: string): string =>
  text ? `${LTR_ISOLATE}${text}${POP_DIRECTIONAL_ISOLATE}` : "";

/**
 * "low – high", ordered by the reader's direction. Either side may be empty (an
 * open-ended range), in which case the other is returned on its own — never a
 * dangling separator.
 */
export function formatBidiRange(low: string, high: string, separator = " - "): string {
  const parts = [low, high].filter(Boolean).map(isolateLtr);
  return parts.join(separator);
}
